import { addDays, addMonths, type LocalDate } from '../dates';
import { singletonId } from '../ids';
import type { DateRange } from '../ledger/counting';
import type { Mutation } from '../mutations';
import { MAX_AMOUNT_MINOR } from '../spending/expense';
import type { BuildCtx } from '../spending/types';
import { isValidLocalDate } from './schedule';
import { budgetMutation, type BudgetResult } from './types';

/**
 * v0.5 "Coming in" (Ivan, 2026-11-03): money expected that isn't income, such as vouchers, a leave
 * conversion or a gift. It is listed on its own, never in income, what's left or safe to spend.
 * Receiving it is a deposit into an account: a ledger row of kind transfer with only toAccountId,
 * so it raises that account's balance and is excluded from income and spending like a transfer.
 */
export interface ExpectedMoneyLike {
  id: string;
  name: string;
  amountMinor: number;
  /** A one-off's day; a repeating item's first occurrence. */
  expectedDate: LocalDate;
  accountId: string | null;
  /** A one-off's status (a repeating item stays 'expected'; its occurrences have receipts). */
  status: 'expected' | 'received';
  receivedDate: LocalDate | null;
  receivedMinor: number | null;
  ledgerEntryId: string | null;
  deletedAt: string | null;
  /** v0.7.4: absent = 'none' (a one-off). */
  repeat?: ExpectedRepeat;
  /** v0.7.4: the last day an occurrence may fall on (null or absent = no end). */
  repeatUntil?: LocalDate | null;
  /** v0.7.4: a repeating item's live receipts (received or skipped occurrences). */
  receipts?: readonly ExpectedReceiptLike[];
}

/**
 * v0.7.4 repeating Coming in (Ivan, 2026-11-04): 'monthly' or 'yearly' from expectedDate, always
 * counted from it (Jan 31 monthly is Feb 28, then Mar 31), up to repeatUntil. Like paydays,
 * occurrences are generated and only the ones acted on are stored (expected_receipts).
 */
export type ExpectedRepeat = 'none' | 'monthly' | 'yearly';
export const EXPECTED_REPEATS: readonly ExpectedRepeat[] = ['none', 'monthly', 'yearly'];

/** One occurrence the user acted on: received (a deposit) or skipped ("Didn't come"). */
export interface ExpectedReceiptLike {
  id: string;
  expectedId: string;
  occurrenceDate: LocalDate;
  status: 'received' | 'skipped';
  receivedDate: LocalDate | null;
  amountMinor: number | null;
  accountId: string | null;
  ledgerEntryId: string | null;
  deletedAt: string | null;
}

export const MAX_EXPECTED_NAME = 60;
/** Generating stops after this many steps (100 years of months). */
const MAX_OCCURRENCES = 1200;

export const repeatOf = (e: Pick<ExpectedMoneyLike, 'repeat'>): ExpectedRepeat =>
  e.repeat ?? 'none';
export const isRepeating = (e: Pick<ExpectedMoneyLike, 'repeat'>): boolean =>
  repeatOf(e) !== 'none';

/** The deposit's id: one per expected item, so two devices receiving it offline write one row. */
export function expectedLedgerId(userId: string, expectedId: string): string {
  return singletonId(userId, `expected.${expectedId}`);
}

/** v0.7.4: a repeating item's receipt row for one occurrence. */
export function expectedReceiptId(userId: string, expectedId: string, date: LocalDate): string {
  return singletonId(userId, `expected_receipts.${expectedId}.${date}`);
}

/** v0.7.4: the deposit of one occurrence of a repeating item. */
export function expectedOccurrenceLedgerId(
  userId: string,
  expectedId: string,
  date: LocalDate,
): string {
  return singletonId(userId, `expected.${expectedId}.${date}`);
}

/** The scheduled days of an item between from and to (inclusive), ascending. */
export function occurrenceDates(
  e: Pick<ExpectedMoneyLike, 'expectedDate' | 'repeat' | 'repeatUntil'>,
  from: LocalDate,
  to: LocalDate,
): LocalDate[] {
  const repeat = repeatOf(e);
  if (repeat === 'none') {
    return e.expectedDate >= from && e.expectedDate <= to ? [e.expectedDate] : [];
  }
  const step = repeat === 'monthly' ? 1 : 12;
  const until = e.repeatUntil ?? null;
  const out: LocalDate[] = [];
  for (let k = 0; k < MAX_OCCURRENCES; k += 1) {
    const d = addMonths(e.expectedDate, k * step);
    if (d > to || (until !== null && d > until)) break;
    if (d >= from) out.push(d);
  }
  return out;
}

export function isOccurrenceOf(
  e: Pick<ExpectedMoneyLike, 'expectedDate' | 'repeat' | 'repeatUntil'>,
  date: LocalDate,
): boolean {
  return isValidLocalDate(date) && occurrenceDates(e, date, date).length === 1;
}

const liveReceipts = (e: ExpectedMoneyLike): ExpectedReceiptLike[] =>
  (e.receipts ?? []).filter((r) => r.deletedAt === null && r.expectedId === e.id);

/** A repeating item's live receipt for one occurrence, or null. */
export function receiptOn(e: ExpectedMoneyLike, date: LocalDate): ExpectedReceiptLike | null {
  return liveReceipts(e).find((r) => r.occurrenceDate === date) ?? null;
}

export interface ExpectedInput {
  name: string;
  amountMinor: number;
  expectedDate: LocalDate;
  accountId: string | null;
  /** v0.7.4: left out = unchanged on an edit, 'none' on an add. */
  repeat?: ExpectedRepeat;
  repeatUntil?: LocalDate | null;
}

export type ExpectedError =
  | 'name_required'
  | 'name_too_long'
  | 'bad_amount'
  | 'bad_date'
  | 'bad_repeat'
  | 'until_before_start'
  | 'has_receipts'
  | 'not_an_occurrence'
  | 'already_received'
  | 'not_received';

const validAmount = (n: number) => Number.isInteger(n) && n > 0 && n <= MAX_AMOUNT_MINOR;

function validate(input: ExpectedInput): ExpectedError | null {
  const name = input.name.trim();
  if (name === '') return 'name_required';
  if (name.length > MAX_EXPECTED_NAME) return 'name_too_long';
  if (!validAmount(input.amountMinor)) return 'bad_amount';
  if (!isValidLocalDate(input.expectedDate)) return 'bad_date';
  if (input.repeat !== undefined && !EXPECTED_REPEATS.includes(input.repeat)) return 'bad_repeat';
  if (input.repeat !== undefined && input.repeat !== 'none' && input.repeatUntil != null) {
    if (!isValidLocalDate(input.repeatUntil)) return 'bad_date';
    if (input.repeatUntil < input.expectedDate) return 'until_before_start';
  }
  return null;
}

function values(input: ExpectedInput): Record<string, unknown> {
  return {
    name: input.name.trim().replace(/\s+/g, ' '),
    amountMinor: input.amountMinor,
    expectedDate: input.expectedDate,
    accountId: input.accountId,
    ...(input.repeat !== undefined
      ? {
          repeat: input.repeat,
          repeatUntil: input.repeat === 'none' ? null : (input.repeatUntil ?? null),
        }
      : {}),
  };
}

export function buildAddExpected(
  input: ExpectedInput,
  ctx: BuildCtx,
): BudgetResult<{ id: string; mutation: Mutation }, ExpectedError> {
  const error = validate(input);
  if (error) return { ok: false, error };
  const id = ctx.newId();
  return {
    ok: true,
    id,
    mutation: budgetMutation(ctx, 'expected_money', 'insert', id, {
      ...values({ ...input, repeat: input.repeat ?? 'none' }),
      status: 'expected',
      receivedDate: null,
      receivedMinor: null,
      ledgerEntryId: null,
      currency: 'PHP',
    }),
  };
}

/**
 * Writes only the fields that changed. A received one-off can't start repeating (its deposit
 * would belong to no occurrence), and a repeating item with received or skipped occurrences
 * can't become a one-off: undo those first.
 */
export function buildEditExpected(
  before: ExpectedMoneyLike,
  input: ExpectedInput,
  ctx: BuildCtx,
): BudgetResult<{ mutation: Mutation | null }, ExpectedError> {
  const error = validate(input);
  if (error) return { ok: false, error };
  const was = repeatOf(before);
  if (input.repeat !== undefined && input.repeat !== was) {
    if (was === 'none' && before.status === 'received') {
      return { ok: false, error: 'already_received' };
    }
    if (input.repeat === 'none' && liveReceipts(before).length > 0) {
      return { ok: false, error: 'has_receipts' };
    }
  }
  const current: Record<string, unknown> = {
    ...(before as unknown as Record<string, unknown>),
    repeat: was,
    repeatUntil: before.repeatUntil ?? null,
  };
  const changed: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values(input))) {
    if (current[k] !== v) changed[k] = v;
  }
  if (Object.keys(changed).length === 0) return { ok: true, mutation: null };
  return {
    ok: true,
    mutation: budgetMutation(ctx, 'expected_money', 'update', before.id, changed),
  };
}

/** What a delete took with it, so Undo brings back the same rows. */
export interface ExpectedRemoved {
  receiptIds: string[];
  ledgerEntryIds: string[];
}

/**
 * Soft delete; a received item's deposit goes with it, and so do a repeating item's receipts and
 * their deposits (all come back on restore with the ids in `removed`).
 */
export function buildDeleteExpected(
  e: ExpectedMoneyLike,
  ctx: BuildCtx,
): { mutations: Mutation[]; removed: ExpectedRemoved } {
  const removed: ExpectedRemoved = { receiptIds: [], ledgerEntryIds: [] };
  if (isRepeating(e)) {
    for (const r of liveReceipts(e)) {
      removed.receiptIds.push(r.id);
      if (r.ledgerEntryId) removed.ledgerEntryIds.push(r.ledgerEntryId);
    }
  } else if (e.status === 'received' && e.ledgerEntryId) {
    removed.ledgerEntryIds.push(e.ledgerEntryId);
  }
  return {
    mutations: [
      budgetMutation(ctx, 'expected_money', 'delete', e.id),
      ...removed.receiptIds.map((id) => budgetMutation(ctx, 'expected_receipts', 'delete', id)),
      ...removed.ledgerEntryIds.map((id) => budgetMutation(ctx, 'ledger_entries', 'delete', id)),
    ],
    removed,
  };
}

/** Brings the item back: a one-off's deposit always, a repeating item's rows from `removed`. */
export function buildRestoreExpected(
  e: ExpectedMoneyLike,
  ctx: BuildCtx,
  removed?: ExpectedRemoved,
): Mutation[] {
  const out = [budgetMutation(ctx, 'expected_money', 'insert', e.id, {})];
  const ledgerIds =
    removed?.ledgerEntryIds ??
    (!isRepeating(e) && e.status === 'received' && e.ledgerEntryId ? [e.ledgerEntryId] : []);
  for (const id of removed?.receiptIds ?? []) {
    out.push(budgetMutation(ctx, 'expected_receipts', 'insert', id, {}));
  }
  for (const id of ledgerIds) out.push(budgetMutation(ctx, 'ledger_entries', 'insert', id, {}));
  return out;
}

type Got = { amountMinor: number; date: LocalDate; accountId: string | null };

function depositMutation(ledgerEntryId: string, e: ExpectedMoneyLike, got: Got, ctx: BuildCtx) {
  return budgetMutation(ctx, 'ledger_entries', 'insert', ledgerEntryId, {
    date: got.date,
    amountMinor: got.amountMinor,
    currency: 'PHP',
    kind: 'transfer',
    categoryId: null,
    method: null,
    fundId: null,
    accountId: null,
    toAccountId: got.accountId,
    sourceModule: 'budget',
    sourceId: e.id,
    note: e.name,
    recordedTz: ctx.tz,
  });
}

/**
 * "Received": the item is marked received (one update with every field) and the deposit is
 * written on its deterministic id with every field in the insert. A plain insert, so receiving
 * again after an undo brings the soft-deleted deposit back with the new values.
 * A repeating item uses buildReceiveOccurrence instead.
 */
export function buildReceiveExpected(
  userId: string,
  e: ExpectedMoneyLike,
  got: Got,
  ctx: BuildCtx,
): BudgetResult<{ mutations: Mutation[]; ledgerEntryId: string | null }, ExpectedError> {
  if (isRepeating(e)) return { ok: false, error: 'not_an_occurrence' };
  if (e.status === 'received') return { ok: false, error: 'already_received' };
  if (!validAmount(got.amountMinor)) return { ok: false, error: 'bad_amount' };
  if (!isValidLocalDate(got.date)) return { ok: false, error: 'bad_date' };
  // Without an account there is nothing to deposit into: the item is just marked received.
  const ledgerEntryId = got.accountId ? expectedLedgerId(userId, e.id) : null;
  const mutations = [
    budgetMutation(ctx, 'expected_money', 'update', e.id, {
      status: 'received',
      receivedDate: got.date,
      receivedMinor: got.amountMinor,
      accountId: got.accountId,
      ledgerEntryId,
    }),
  ];
  if (ledgerEntryId) mutations.push(depositMutation(ledgerEntryId, e, got, ctx));
  return { ok: true, mutations, ledgerEntryId };
}

/** Undo "Received": expected again, and the deposit is soft-deleted. */
export function buildUndoReceiveExpected(
  e: ExpectedMoneyLike,
  ctx: BuildCtx,
): BudgetResult<{ mutations: Mutation[] }, ExpectedError> {
  if (isRepeating(e) || e.status !== 'received') return { ok: false, error: 'not_received' };
  const mutations = [
    budgetMutation(ctx, 'expected_money', 'update', e.id, {
      status: 'expected',
      receivedDate: null,
      receivedMinor: null,
      ledgerEntryId: null,
    }),
  ];
  if (e.ledgerEntryId) {
    mutations.push(budgetMutation(ctx, 'ledger_entries', 'delete', e.ledgerEntryId));
  }
  return { ok: true, mutations };
}

function checkOccurrence(e: ExpectedMoneyLike, date: LocalDate): ExpectedError | null {
  if (!isRepeating(e) || !isOccurrenceOf(e, date)) return 'not_an_occurrence';
  if (receiptOn(e, date)?.status === 'received') return 'already_received';
  return null;
}

/**
 * v0.7.4 "Received" on one occurrence of a repeating item: a receipt row and (with an account) a
 * deposit, both on deterministic ids with every field in the insert, so two devices converge and
 * receiving again after an undo (or after a skip) brings the rows back with the new values.
 * The later occurrences stay expected.
 */
export function buildReceiveOccurrence(
  userId: string,
  e: ExpectedMoneyLike,
  occurrenceDate: LocalDate,
  got: Got,
  ctx: BuildCtx,
): BudgetResult<
  { mutations: Mutation[]; receiptId: string; ledgerEntryId: string | null },
  ExpectedError
> {
  const error = checkOccurrence(e, occurrenceDate);
  if (error) return { ok: false, error };
  if (!validAmount(got.amountMinor)) return { ok: false, error: 'bad_amount' };
  if (!isValidLocalDate(got.date)) return { ok: false, error: 'bad_date' };
  const receiptId = expectedReceiptId(userId, e.id, occurrenceDate);
  const ledgerEntryId = got.accountId
    ? expectedOccurrenceLedgerId(userId, e.id, occurrenceDate)
    : null;
  const mutations = [
    budgetMutation(ctx, 'expected_receipts', 'insert', receiptId, {
      expectedId: e.id,
      occurrenceDate,
      status: 'received',
      receivedDate: got.date,
      amountMinor: got.amountMinor,
      accountId: got.accountId,
      ledgerEntryId,
      currency: 'PHP',
    }),
  ];
  if (ledgerEntryId) mutations.push(depositMutation(ledgerEntryId, e, got, ctx));
  return { ok: true, mutations, receiptId, ledgerEntryId };
}

/** v0.7.4 "Didn't come": one occurrence is skipped; the later ones stay expected. */
export function buildSkipOccurrence(
  userId: string,
  e: ExpectedMoneyLike,
  occurrenceDate: LocalDate,
  ctx: BuildCtx,
): BudgetResult<{ mutation: Mutation; receiptId: string }, ExpectedError> {
  const error = checkOccurrence(e, occurrenceDate);
  if (error) return { ok: false, error };
  const receiptId = expectedReceiptId(userId, e.id, occurrenceDate);
  return {
    ok: true,
    receiptId,
    mutation: budgetMutation(ctx, 'expected_receipts', 'insert', receiptId, {
      expectedId: e.id,
      occurrenceDate,
      status: 'skipped',
      receivedDate: null,
      amountMinor: null,
      accountId: null,
      ledgerEntryId: null,
      currency: 'PHP',
    }),
  };
}

/** v0.7.4: undo a received or skipped occurrence (expected again; its deposit soft-deleted). */
export function buildUndoOccurrence(
  e: ExpectedMoneyLike,
  occurrenceDate: LocalDate,
  ctx: BuildCtx,
): BudgetResult<{ mutations: Mutation[] }, ExpectedError> {
  const r = receiptOn(e, occurrenceDate);
  if (!r) return { ok: false, error: 'not_received' };
  const mutations = [budgetMutation(ctx, 'expected_receipts', 'delete', r.id)];
  if (r.ledgerEntryId) {
    mutations.push(budgetMutation(ctx, 'ledger_entries', 'delete', r.ledgerEntryId));
  }
  return { ok: true, mutations };
}

export interface ComingInItem {
  /** The item's id. */
  id: string;
  /** Unique per occurrence (list keys). */
  key: string;
  name: string;
  /** Received amount once received, else the expected amount. */
  amountMinor: number;
  /** The day it came, else the day it's expected. */
  date: LocalDate;
  /** The scheduled day: a one-off's expected day, or this occurrence's. */
  occurrenceDate: LocalDate;
  accountId: string | null;
  status: 'expected' | 'received';
  /** Expected on a day already past and not received yet ("expected, not yet received"). */
  late: boolean;
  repeat: ExpectedRepeat;
}

const byDate = (a: ComingInItem, b: ComingInItem) =>
  a.date < b.date ? -1 : a.date > b.date ? 1 : a.key < b.key ? -1 : a.key > b.key ? 1 : 0;

function expectedItem(e: ExpectedMoneyLike, date: LocalDate, today: LocalDate): ComingInItem {
  return {
    id: e.id,
    key: `${e.id}|${date}`,
    name: e.name,
    amountMinor: e.amountMinor,
    date,
    occurrenceDate: date,
    accountId: e.accountId,
    status: 'expected',
    late: date < today,
    repeat: repeatOf(e),
  };
}

/** A repeating item's occurrences not received or skipped, between from and to. */
function openDates(e: ExpectedMoneyLike, from: LocalDate, to: LocalDate): LocalDate[] {
  const acted = new Set(liveReceipts(e).map((r) => r.occurrenceDate));
  return occurrenceDates(e, from, to).filter((d) => !acted.has(d));
}

/**
 * "Coming in" for a period: received items by the day they came, expected ones by their expected
 * day. In the period holding today, expected items whose day has passed are listed too (late),
 * so they stay in view until they're received or deleted. Never added to any total of the period.
 * v0.7.4: a repeating item lists each of its occurrences (skipped ones are left out).
 */
export function comingIn(
  items: readonly ExpectedMoneyLike[],
  range: DateRange,
  today: LocalDate,
): ComingInItem[] {
  const current = range.start <= today && today <= range.end;
  const inRange = (d: LocalDate) => d >= range.start && d <= range.end;
  const out: ComingInItem[] = [];
  for (const e of items) {
    if (e.deletedAt !== null) continue;
    if (isRepeating(e)) {
      for (const r of liveReceipts(e)) {
        const date = r.receivedDate ?? r.occurrenceDate;
        if (r.status !== 'received' || !inRange(date)) continue;
        out.push({
          ...expectedItem(e, r.occurrenceDate, today),
          amountMinor: r.amountMinor ?? e.amountMinor,
          date,
          accountId: r.accountId,
          status: 'received',
          late: false,
        });
      }
      for (const d of openDates(e, current ? e.expectedDate : range.start, range.end)) {
        const item = expectedItem(e, d, today);
        if (inRange(d) || (current && item.late)) out.push(item);
      }
      continue;
    }
    const received = e.status === 'received';
    const date = received ? (e.receivedDate ?? e.expectedDate) : e.expectedDate;
    const late = !received && e.expectedDate < today;
    if (!inRange(date) && !(current && late && date < range.start)) continue;
    out.push({
      ...expectedItem(e, e.expectedDate, today),
      amountMinor: received ? (e.receivedMinor ?? e.amountMinor) : e.amountMinor,
      date,
      status: e.status,
      late,
    });
  }
  return out.sort(byDate);
}

/** How far ahead (in months, per occurrence wanted) the next occurrences are looked for. */
const LOOKAHEAD_MONTHS = 13;

/**
 * An item's open occurrences: every late one (day passed, not received or skipped) and the next
 * `upcoming` from today on. A one-off still expected is its one occurrence.
 */
export function openOccurrences(
  e: ExpectedMoneyLike,
  today: LocalDate,
  upcoming = 1,
): ComingInItem[] {
  if (e.deletedAt !== null) return [];
  if (!isRepeating(e)) {
    return e.status === 'expected' ? [expectedItem(e, e.expectedDate, today)] : [];
  }
  const late = openDates(e, e.expectedDate, addDays(today, -1));
  const next = openDates(e, today, addMonths(today, LOOKAHEAD_MONTHS * Math.max(1, upcoming)));
  return [...late, ...next.slice(0, upcoming)].map((d) => expectedItem(e, d, today));
}

/**
 * Everything still expected, oldest first: the Coming in card. A repeating item shows its late
 * occurrences and the next one (the one after shows up once that one is received).
 */
export function stillExpected(
  items: readonly ExpectedMoneyLike[],
  today: LocalDate,
): ComingInItem[] {
  return items.flatMap((e) => openOccurrences(e, today, 1)).sort(byDate);
}

/** A repeating item's received occurrences, newest first (its sheet lists them with Undo). */
export function receivedOccurrences(e: ExpectedMoneyLike): ExpectedReceiptLike[] {
  return liveReceipts(e)
    .filter((r) => r.status === 'received')
    .sort((a, b) => (a.occurrenceDate < b.occurrenceDate ? 1 : -1));
}
